import PublicFormView from "@/pages/forms/PublicFormView";
/**
 * HustleAnswerSalesPage.jsx — bare-domain sales page for hustleanswer.au.
 *
 * The Hustle Ai Receptionist product page. Renamed from "AI Receptionist"
 * (the vendor add-on that shipped inside HustlePlate). Same engine —
 * standalone brand. Works for restaurants, salons, tradies, medical,
 * property managers, tourism operators — any Aussie SMB that answers
 * a phone.
 *
 * Dispatched by RootRouter on hustleanswer.au + www.hustleanswer.au.
 * Registered in App.js as /hustleanswer for the preview / cross-link
 * path from hustlesuite.au.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FlipCard from "@/components/FlipCard";
import {
  ArrowRight, PhoneCall, Bot, Sparkles, ShieldCheck, Clock, CalendarDays,
  Volume2, Mic, Languages, MessageSquare, Zap, Globe, Send, CheckCircle2,
  Store, HeartHandshake, Rocket, Wrench,
} from "lucide-react";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1587560699334-cc4ff634909a?auto=format&fit=crop&w=1600&q=70";
const IMG_HANDOFF =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=70";
const IMG_CHANNELS =
  "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1400&q=70";
const IMG_WORLD =
  "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1400&q=70";

const HIGHLIGHTS = [
  { icon: PhoneCall,   title: "Answered on the 2nd ring",
    text: "Never miss another booking. Every inbound call is picked up in under 3 seconds — 24/7." },
  { icon: Languages,   title: "12 languages, code-switch aware",
    text: "Answers in the caller's language and switches mid-call if they do. Every booking is language-stamped." },
  { icon: Mic,         title: "Your voice, cloned",
    text: "Train the AI on a 30-second sample of your receptionist so callers never feel outsourced." },
  { icon: ShieldCheck, title: "Allergen + keyword safe",
    text: "Hears the 14 major allergens in every accent. Flagged keywords (complaint, sick, refund) auto-escalate to a human." },
];

const USE_CASES = [
  { icon: Store,        title: "Restaurants & cafés",
    body: "Takes bookings live against your Table Builder floor plan. Confirms allergies. Sends SMS confirmations before the caller hangs up. Escalates complaints straight to the manager's mobile." },
  { icon: HeartHandshake,title: "Salons, spas & clinics",
    body: "Books, reschedules and cancels appointments against your existing calendar. Handles insurance / rebate questions from a script you approve once." },
  { icon: Wrench,       title: "Trades & services",
    body: "Sparkies, plumbers, mechanics — nobody misses a call again. The AI captures job type, urgency, address, and drops a booking straight into HustleCRM." },
  { icon: Rocket,       title: "Property & tourism",
    body: "Property managers get 24/7 leasing enquiries answered in the caller's language. Tourism operators book tours, take deposits and email itineraries in one call." },
];

const FEATURES = [
  { icon: PhoneCall,     label: "Answer inbound calls, 2nd ring" },
  { icon: Languages,     label: "12 languages, code-switch aware" },
  { icon: Mic,           label: "Neural voice cloning from a 30-sec sample" },
  { icon: CalendarDays,  label: "Live booking against your floor plan / calendar" },
  { icon: MessageSquare, label: "SMS + email + Instagram DM + WhatsApp" },
  { icon: ShieldCheck,   label: "Allergen + keyword capture" },
  { icon: Zap,           label: "Escalate flagged calls to a real human" },
  { icon: Clock,         label: "24 / 7 / 365, incl. Christmas Day" },
  { icon: Globe,         label: "Time-zone aware for tourist bookings" },
  { icon: Sparkles,      label: "Post-call summary + CRM update" },
  { icon: Volume2,       label: "30 pre-made voices (AU / UK / US / multicultural)" },
  { icon: Bot,           label: "Draft-and-review mode for the tricky calls" },
];

export default function HustleAnswerSalesPage() {

  useEffect(() => {
    document.title = "HustleAnswer — The Hustle Ai Receptionist";
  }, []);


  return (
    <div
      data-testid="hustleanswer-sales-page"
      className="min-h-screen bg-white text-stone-900 dark:bg-[#07131a] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader salesTopGap
        brand={{ name: "HustleAnswer", to: "/" }}
        nav={[
          { to: "/",                     label: "Home" },
          { to: "/hustleanswer/demo",    label: "Live demo" },
          { to: "#features",             label: "Features" },
          { to: "#enquire",              label: "Get a quote" },
          { to: "/hustleanswer/app",     label: "Configure", align: "right", variant: "primary" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(8,145,178,0.18),transparent_60%)]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-12 lg:pt-24 lg:pb-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-400 mb-5">
              The Hustle Ai Receptionist
            </p>
            <h1
              data-testid="answer-hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight"
            >
              Answers every call. Second ring. Twelve languages.
              <span className="text-cyan-500"> Zero missed bookings.</span>
            </h1>
            <p
              data-testid="answer-hero-subtitle"
              className="text-base md:text-lg text-stone-600 dark:text-stone-300 mt-6 max-w-xl"
            >
              HustleAnswer is a real-voice Ai receptionist for Australian small businesses.
              It picks up your phone in the caller's language, takes live bookings against
              your calendar, sends SMS confirmations before they hang up, and only escalates
              the calls that need a human.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/hustleanswer/demo" data-testid="answer-hero-demo-cta">
                <Button className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-stone-950 font-semibold px-7 h-12 text-base">
                  Try the live demo
                  <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
                </Button>
              </Link>
              <a href="#enquire" data-testid="answer-hero-enquire-cta">
                <Button
                  variant="outline"
                  className="rounded-full border-stone-300 dark:border-stone-700 bg-transparent text-stone-800 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 font-semibold px-7 h-12 text-base"
                >
                  Get a quote
                </Button>
              </a>
              <Link to="/hustleanswer/app" data-testid="answer-hero-configure-cta">
                <Button
                  variant="ghost"
                  className="rounded-full text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 font-semibold px-7 h-12 text-base"
                >
                  Configure
                </Button>
              </Link>
            </div>
            <p className="text-xs text-stone-500 dark:text-stone-500 mt-6 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-cyan-500" strokeWidth={1.5} />
              Australian-owned · onshore data · included with HustlePlate Pro and above
            </p>
          </div>
          <div className="relative">
            <img
              src={HERO_IMG}
              alt="Modern phone lit up mid-call in a busy Australian dining room"
              data-testid="answer-hero-image"
              className="rounded-2xl shadow-2xl shadow-cyan-950/40 border border-stone-200 dark:border-stone-800 w-full aspect-[4/3] object-cover"
            />
            <div className="absolute -bottom-4 -right-4 bg-cyan-500 text-stone-950 px-4 py-2 rounded-full shadow-lg text-xs font-bold flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5" />
              Live in 5 business days
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20" data-testid="answer-highlights">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 p-6 hover:border-cyan-600/60 transition-colors"
            >
              <Icon className="h-6 w-6 text-cyan-500 mb-4" strokeWidth={1.5} />
              <h3 className="font-semibold text-stone-900 dark:text-stone-100">{title}</h3>
              <p className="text-sm text-stone-600 dark:text-stone-400 mt-2">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The 27% loss story */}
      <section
        id="story"
        data-testid="answer-story"
        className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/60"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 lg:py-20 space-y-6 text-stone-700 dark:text-stone-300 leading-relaxed">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-500">
            Why HustleAnswer
          </p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 dark:text-white leading-tight">
            27% of your inbound calls go to voicemail. That's your rent, funded by nobody.
          </h2>
          <p>
            The Restaurant Consultants Australia 2024 tracker found that the
            average independent venue misses 27% of its booking calls during
            service — dropped to voicemail because the front-of-house was
            already busy. At an average table-of-four spending $180, that's
            about <strong>$2,400 a month</strong> in bookings that never
            happen because your phone rang four times, dropped, and the
            caller had already Googled a competitor by the time they hung
            up.
          </p>
          <p>
            HustleAnswer picks up on the second ring, in the caller's
            language, in a voice you either record yourself or pick from
            our library, and books the table live against your existing
            calendar. Every call gets an SMS confirmation to the guest
            before they hang up. The system only escalates to a human
            when it hears words <em>you</em> flagged — <q>complaint</q>,
            <q>sick</q>, <q>lost property</q>, <q>manager</q>.
          </p>
          <img
            src={IMG_HANDOFF}
            alt="A caller on a phone at night booking a restaurant"
            data-testid="answer-story-img"
            className="rounded-xl shadow-lg my-6 w-full aspect-[16/9] object-cover"
          />
        </div>
      </section>

      {/* Flip cards */}
      <section
        id="features"
        data-testid="answer-flip-row"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20"
      >
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-500 mb-4">
          What it does
        </p>
        <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight max-w-3xl">
          Everything a great human host does. Just faster, and at 3am.
        </h2>
        <p className="text-xs text-stone-500 dark:text-stone-500 my-4 italic">
          Hover any card for the long version · tap on mobile.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <FlipCard
            height={240}
            testid="answer-flip-247"
            front={<FlipFront icon={Clock} title="24 / 7"
              subtitle="Bookings at 2am. Confirmed by 2:00:12am." />}
            back={<FlipBack body="Tourists, shift workers, late planners — restaurants get calls at 2am more often than most owners realise. HustleAnswer answers, confirms, SMSes and drops the booking into your morning run sheet before the espresso machine turns on." />}
          />
          <FlipCard
            height={240}
            testid="answer-flip-allergens"
            front={<FlipFront icon={ShieldCheck} title="Allergen-safe"
              subtitle="Recognises + records every allergy warning." />}
            back={<FlipBack body='The Ai knows all 14 major allergens and hears them in every accent — "nuts", "noix", "kacang", "花生". Anything captured lands as a red-flag note on the booking, propagates to the KDS, and forces a confirm-clean step in the kitchen.' />}
          />
          <FlipCard
            height={240}
            testid="answer-flip-escalate"
            front={<FlipFront icon={PhoneCall} title="Smart escalation"
              subtitle="Complaint? Sick? Straight to a human." />}
            back={<FlipBack body="Any keyword you flag (complaint, food poisoning, refund, lost property, sick, hospital) triggers instant transfer to a real human phone — your manager's mobile, if that's what you want. Full transcript sent by SMS so they arrive already briefed." />}
          />
        </div>
      </section>

      {/* Voice + channels */}
      <section
        data-testid="answer-voice-channels"
        className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/60"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 lg:py-20 space-y-6 text-stone-700 dark:text-stone-300 leading-relaxed">
          <h2 className="text-3xl font-extrabold text-stone-900 dark:text-white flex items-center gap-3">
            <Volume2 className="h-7 w-7 text-cyan-500" />
            Your voice. Your accent. Your welcome.
          </h2>
          <p>
            Guests can tell an off-the-shelf robot voice in under three
            seconds — and half of them will hang up. HustleAnswer uses a
            neural voice engine that can be trained on a 30-second sample
            of your actual receptionist saying <q>Hi, it's Ella from Caffè
            Roma, thanks for calling</q>, and reproduces her entire voice
            for the Ai to speak in. Guests never feel like they've been
            outsourced to a call centre. Ella isn't at the desk on a
            Tuesday? The system still sounds like her.
          </p>
          <p>
            Prefer not to clone a voice? We ship a library of 30 Australian,
            British, American and multicultural voices — each profiled with
            a warmth score, formality score, and accent score. Pick the one
            that fits your brand.
          </p>

          <img
            src={IMG_WORLD}
            alt="World map with connected pins"
            data-testid="answer-world-img"
            className="rounded-xl shadow-lg my-8 w-full aspect-[16/9] object-cover"
          />

          <h3 className="text-2xl font-bold text-stone-900 dark:text-white mt-10">
            Beyond the phone — SMS, email, DM, WhatsApp.
          </h3>
          <p>
            The receptionist isn't just for the phone. The same brain
            answers your Instagram DMs, your Facebook messenger, your
            WhatsApp Business account, your Google Business Q&amp;A, and
            the enquiry form on your website. Guests get an answer within
            30 seconds regardless of channel. Every response is logged in
            HustleCRM so when the guest arrives tonight your hostess
            already knows they asked about a birthday cake option three
            days ago on Instagram.
          </p>
          <img
            src={IMG_CHANNELS}
            alt="A phone showing multiple chat apps stacked"
            data-testid="answer-channels-img"
            className="rounded-xl shadow-lg mt-8 w-full aspect-[16/9] object-cover"
          />
        </div>
      </section>

      {/* Use cases (tabs) */}
      <section
        id="usecases"
        data-testid="answer-usecases"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20"
      >
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-500 mb-4">
          Who it's for
        </p>
        <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight max-w-3xl mb-8">
          Any Aussie business that answers a phone.
        </h2>
        <Tabs defaultValue="restaurants" className="w-full" data-testid="answer-usecase-tabs">
          <TabsList className="grid w-full max-w-3xl grid-cols-2 sm:grid-cols-4 mb-8" data-testid="answer-tablist">
            {USE_CASES.map(({ icon: Icon, title }) => (
              <TabsTrigger
                key={title}
                value={title.split(" ")[0].toLowerCase()}
                data-testid={`answer-tab-${title.split(" ")[0].toLowerCase()}`}
              >
                <Icon className="h-4 w-4 mr-2" strokeWidth={1.75} />
                {title.split(" ")[0]}
              </TabsTrigger>
            ))}
          </TabsList>
          {USE_CASES.map(({ icon: Icon, title, body }) => (
            <TabsContent
              key={title}
              value={title.split(" ")[0].toLowerCase()}
              data-testid={`answer-content-${title.split(" ")[0].toLowerCase()}`}
            >
              <div className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/40 p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-stone-900 dark:text-stone-100">
                      {title}
                    </h3>
                    <p className="text-sm text-stone-700 dark:text-stone-300 mt-2 leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* Feature grid */}
      <section
        data-testid="answer-feature-grid"
        className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/60"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-500 mb-4">
            What's in the box
          </p>
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight max-w-3xl">
            Everything that used to cost 12 vendors.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {FEATURES.map((f) => (
              <div
                key={f.label}
                data-testid={`answer-feature-${f.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="bg-white dark:bg-stone-900 rounded-xl p-5 shadow-sm border border-stone-100 dark:border-stone-800 flex items-start gap-3"
              >
                <div className="h-10 w-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div className="text-sm text-stone-800 dark:text-stone-200 font-medium leading-relaxed">
                  {f.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
        <blockquote className="border-l-4 border-cyan-500 pl-5 py-3 italic text-lg text-stone-800 dark:text-stone-200">
          "Booked 41 extra covers in the first month from calls that would
          have gone to voicemail. Half of them are now regulars. One of
          them is a Cantonese food critic who wrote us up in a Sydney
          magazine because HustleAnswer took his booking in Cantonese on
          a Wednesday night."
          <footer className="mt-3 text-sm not-italic text-stone-500 dark:text-stone-500">
            — May, owner, 55-seat modern-Chinese restaurant, Chatswood
          </footer>
        </blockquote>
      </section>

      {/* Where it fits inside HustleSuite */}
      <section
        data-testid="answer-suite-fit"
        className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/60"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-500 mb-4">
            Where HustleAnswer fits
          </p>
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight max-w-3xl">
            Included with HustlePlate Pro + above. Standalone plans available.
          </h2>
          <p className="text-stone-600 dark:text-stone-400 mt-4 max-w-2xl">
            Already on HustlePlate Pro or Enterprise? HustleAnswer is already
            baked in — head to the Configure tab to switch it on. Not a
            HustlePlate customer? HustleAnswer plugs into any calendar
            (Google, Microsoft, Apple, Cal.com, Calendly), any CRM, and any
            phone number. We port your existing number in 5 business days.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/40 p-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-500 mb-2">
                Included
              </p>
              <p className="font-bold text-stone-900 dark:text-stone-100">HustlePlate Pro · Enterprise</p>
              <p className="text-sm text-stone-600 dark:text-stone-400 mt-2">
                Ships bundled with the restaurant back-office.
              </p>
            </div>
            <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/40 p-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-500 mb-2">
                Standalone
              </p>
              <p className="font-bold text-stone-900 dark:text-stone-100">From $149 / month</p>
              <p className="text-sm text-stone-600 dark:text-stone-400 mt-2">
                Any Aussie SMB — no HustlePlate account required.
              </p>
            </div>
            <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/40 p-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-500 mb-2">
                Add-on
              </p>
              <p className="font-bold text-stone-900 dark:text-stone-100">HustleCRM · HustleStays · HustleConnect</p>
              <p className="text-sm text-stone-600 dark:text-stone-400 mt-2">
                One tick from your Suite Member Settings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section id="enquire" className="border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
              Get HustleAnswer answering your calls this week.
            </h2>
            <p className="text-stone-600 dark:text-stone-400 mt-4 max-w-md">
              Tell us the business and we'll come back with a quote and a
              go-live plan. Number porting handled for you. Voice clone
              recorded during the onboarding call.
            </p>
          </div>
          <div className="rounded-xl border border-stone-200 bg-white p-6"><PublicFormView inline formSlug="hustleanswer-enquiry" sourceUrl={window.location.origin+window.location.pathname} /></div>
        </div>
      </section>

      <SuiteFooter />
    </div>
  );
}

function FlipFront({ icon: Icon, title, subtitle }) {
  return (
    <div className="h-full w-full bg-gradient-to-br from-cyan-600 to-sky-800 text-white p-6 flex flex-col justify-between rounded-xl">
      <Icon className="h-10 w-10" strokeWidth={1.5} />
      <div>
        <div className="text-2xl font-bold">{title}</div>
        <div className="text-cyan-100 text-sm mt-1">{subtitle}</div>
      </div>
    </div>
  );
}
function FlipBack({ body }) {
  return (
    <div className="h-full w-full bg-white dark:bg-stone-900 p-5 flex items-center text-sm text-stone-700 dark:text-stone-300 leading-relaxed rounded-xl">
      {body}
    </div>
  );
}
